import { FC } from "react";
import { Box, HStack, IconButton, Text } from "@chakra-ui/react";
import { useControls } from "react-zoom-pan-pinch";

import { AddIcon, ChevronLeftIcon, DownloadIcon, EditIcon, MinusIcon } from "@shared/components/Icons";
import { generateFormInitialValues } from "@containers/Cases/components/CaseViewContainer/MachineAnalysis/MachineAnalysis.utils";
import { useDownloadCaseForm } from "@shared/hooks";
import { getFullName } from "@shared/utils";
import { MachineAnalysisDownload } from "@containers/Cases/components/CaseViewContainer/MachineAnalysis/MachineAnalysisDownload";

export interface MachineAnalysisPreviewModalFooterControlsProps {
  onAddPage: () => void;
  onSubtractPage: () => void;
  maxPage: number;
  minPage: number;
  currentPage: number;
  scale: number;
  onClose: () => void;
  caseObjUpdatedValues: ReturnType<typeof generateFormInitialValues>;
}

const MachineAnalysisPreviewModalFooterControls: FC<MachineAnalysisPreviewModalFooterControlsProps> = ({
  onAddPage,
  onSubtractPage,
  maxPage,
  minPage,
  currentPage,
  scale,
  onClose,
  caseObjUpdatedValues,
}) => {
  const { zoomIn, zoomOut } = useControls();

  const { handleDownloadPaf, isDownloading, targetRef } = useDownloadCaseForm(getFullName(caseObjUpdatedValues, ""));

  return (
    <HStack px={4} py={2.5} bg="blue.900" borderRadius={8} separator={<Box w={0.2} h="100%" />}>
      <HStack textStyle="label" color="white">
        <IconButton h="auto" aria-label={"zoom-in"} onClick={() => zoomOut()} variant="transparent">
          <MinusIcon color="white" boxSize={2} />
        </IconButton>
        <Text>{scale}%</Text>
        <IconButton h="auto" aria-label={"zoom-in"} onClick={() => zoomIn()} variant="transparent">
          <AddIcon color="white" boxSize={2} />
        </IconButton>
      </HStack>

      <HStack gap={2}>
        <IconButton
          h="auto"
          aria-label={"Previous"}
          onClick={onSubtractPage}
          disabled={currentPage === minPage}
          variant="transparent"
        >
          <ChevronLeftIcon color="white" boxSize={4} />
        </IconButton>
        <HStack gap={1} textStyle="label" color="white">
          <Text>{currentPage}</Text>
          <Text>/</Text>
          <Text>{maxPage}</Text>
        </HStack>
        <IconButton
          h="auto"
          aria-label={"Next"}
          onClick={onAddPage}
          disabled={currentPage === maxPage}
          variant="transparent"
        >
          <ChevronLeftIcon boxSize={4} color="white" transform="rotate(180deg)" />
        </IconButton>
      </HStack>

      <HStack gap={1}>
        <IconButton h="auto" aria-label={"edit"} onClick={onClose} variant="transparent">
          <EditIcon color="white" />
        </IconButton>
        <IconButton h="auto" aria-label={"download"} onClick={handleDownloadPaf} variant="transparent">
          <DownloadIcon color="white" />
        </IconButton>
      </HStack>
      {!!caseObjUpdatedValues && isDownloading && (
        <MachineAnalysisDownload targetRef={targetRef} caseObjUpdatedValues={caseObjUpdatedValues} />
      )}
    </HStack>
  );
};

export default MachineAnalysisPreviewModalFooterControls;
